import React from 'react'
import { Alert, Box, Chip, Grid, LinearProgress, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material'
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded'
import AppsRoundedIcon from '@mui/icons-material/AppsRounded'
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded'
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded'
import SyncRoundedIcon from '@mui/icons-material/SyncRounded'
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded'
import { motion } from 'motion/react'
import { NavLink } from 'react-router-dom'
import { cardTransition, listItemTransition } from '../../components/animation/motionPresets'
import { MetricCard } from '../../components/dashboard/MetricCard'
import { useAuth } from '../../context/AuthContext'
import { API_REQUESTS_PAUSED } from '../../data'
import { getApplications } from '../../services/admin/applicationsApi'
import { getIntegrations } from '../../services/admin/integrationsApi'
import { getUserPanelApplicationIcon } from '../../services/user/userActivity'
import { getAssignedApplicationLinks } from '../../services/user/sessionSelectors'
import { getApiErrorMessage, getPaginatedItems } from '../../utils/apiData'
import {
  buildExecutionMetrics,
  flattenIntegrationAlerts,
  getTodayExecutions,
  getUpcomingSchedules,
  getProcessChartBackground,
} from '../../utils/adminIntegrationData'

const AdminFooterLink = ({ children, path }) => (
  <Box component={path ? NavLink : 'div'} to={path} className="admin-panel-footer-link">
    <Typography>{children}</Typography>
    <ArrowForwardRoundedIcon sx={{ fontSize: 18 }} />
  </Box>
)

const buildAdminMetrics = ({ applications, integrations, executions, alerts, schedules }) => ([
  { title: 'Aplicaciones', value: String(applications.length), action: 'Ver todas', icon: <AppsRoundedIcon />, path: '/admin/aplicaciones' },
  { title: 'Integraciones', value: String(integrations.length), action: 'Ver integraciones', icon: <SyncRoundedIcon />, path: '/admin/integraciones' },
  { title: 'Procesos hoy', value: String(executions.length), action: 'Ver histórico', icon: <CheckCircleOutlineRoundedIcon />, path: '/admin/procesos-hoy' },
  { title: 'Procesos con error', value: String(alerts.filter((alert) => alert.tone === 'error').length), action: 'Ver detalles', icon: <WarningAmberRoundedIcon />, path: '/admin/procesos-error' },
  { title: 'Próximas ejecuciones', value: String(schedules.length), action: 'Ver calendario', icon: <AccessTimeRoundedIcon />, path: '/admin/proximas-ejecuciones' },
])

const buildProcessSummary = (executions) => {
  const metrics = buildExecutionMetrics(executions)
  const total = executions.length
  const pending = Math.max(0, total - metrics.completed - metrics.warnings - metrics.errors)
  const toPercent = (value) => (total > 0 ? `${((value / total) * 100).toFixed(1).replace('.', ',')} %` : '0 %')

  return [
    { label: 'Completados', value: String(metrics.completed), percent: toPercent(metrics.completed), color: '#08bf5b' },
    { label: 'Con advertencias', value: String(metrics.warnings), percent: toPercent(metrics.warnings), color: '#ffb020' },
    { label: 'Con errores', value: String(metrics.errors), percent: toPercent(metrics.errors), color: '#ff4d4f' },
    { label: 'Pendientes', value: String(pending), percent: toPercent(pending), color: '#c8ccd1' },
  ]
}

const mapApplicationToQuickApp = (application) => ({
  ...application,
  icon: getUserPanelApplicationIcon(application),
})

export const AdminDashboard = () => {
  const { session } = useAuth()
  const [applications, setApplications] = React.useState([])
  const [integrations, setIntegrations] = React.useState([])
  const [loading, setLoading] = React.useState(false)
  const [errorMessage, setErrorMessage] = React.useState('')

  React.useEffect(() => {
    if (API_REQUESTS_PAUSED) return undefined

    let active = true

    const loadDashboard = async () => {
      setLoading(true)
      setErrorMessage('')

      try {
        const [applicationsResponse, integrationsResponse] = await Promise.all([
          getApplications({ page: 1, limit: 500, sortDirection: 'asc' }),
          getIntegrations({ page: 1, limit: 500, sortDirection: 'asc' }),
        ])

        if (!active) return
        setApplications(getPaginatedItems(applicationsResponse).filter((application) => application.active !== false))
        setIntegrations(getPaginatedItems(integrationsResponse))
      } catch (error) {
        if (active) setErrorMessage(getApiErrorMessage(error, 'No se ha podido cargar el panel de administrador.'))
      } finally {
        if (active) setLoading(false)
      }
    }

    loadDashboard()

    return () => {
      active = false
    }
  }, [])

  const executions = React.useMemo(() => getTodayExecutions(integrations), [integrations])
  const alerts = React.useMemo(() => flattenIntegrationAlerts(integrations), [integrations])
  const schedules = React.useMemo(() => getUpcomingSchedules(integrations), [integrations])
  const adminMetrics = buildAdminMetrics({ applications, integrations, executions, alerts, schedules })
  const processSummary = buildProcessSummary(executions)
  const quickApps = getAssignedApplicationLinks(session).map(mapApplicationToQuickApp)
  const visibleExecutions = executions.slice(0, 6)
  const activeAlerts = alerts.slice(0, 3)
  const schedule = schedules.slice(0, 5)

  return (
    <Box className="page-fade">
      <Box className="page-heading">
        <Typography variant="h4">Bienvenido, Administrador</Typography>
        <Typography>Desde aquí puedes acceder a todas las aplicaciones y consultar la información general de la plataforma.</Typography>
      </Box>

      {loading && <LinearProgress sx={{ mb: 1.5 }} />}
      {errorMessage && <Alert severity="warning" sx={{ mb: 1.5 }}>{errorMessage}</Alert>}

      <Grid container spacing={2.1}>
        {adminMetrics.map((metric) => (
          <Grid key={metric.title} size={{ xs: 12, sm: 6, lg: 2.4 }}>
            <MetricCard {...metric} />
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2.1} sx={{ mt: 1.2 }}>
        <Grid size={{ xs: 12, lg: 5 }}>
          <Box component={motion.div} className="dashboard-section-card admin-panel-card" {...cardTransition}>
            <Typography className="section-title">Resumen de procesos de integración</Typography>
            <Box className="admin-process-summary">
              <Box
                className="admin-donut"
                style={{ background: getProcessChartBackground(processSummary, executions.length) }}
                role="img"
                aria-label={`Procesos de hoy: ${executions.length}. ${processSummary.map((item) => `${item.label}: ${item.value} (${item.percent})`).join('. ')}`}
              >
                <Box>
                  <Typography className="admin-donut-value">{executions.length}</Typography>
                  <Typography className="admin-donut-label">Total hoy</Typography>
                </Box>
              </Box>
              <Box className="admin-process-legend">
                {processSummary.map((item, index) => (
                  <Box component={motion.div} key={item.label} className="admin-process-legend-row" {...listItemTransition(index)}>
                    <span style={{ backgroundColor: item.color }} />
                    <Typography>{item.label}</Typography>
                    <Typography>{item.value} ({item.percent})</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
            <AdminFooterLink path="/admin/integraciones">Ver panel de integraciones</AdminFooterLink>
          </Box>
        </Grid>

        <Grid size={{ xs: 12, lg: 7 }}>
          <Box component={motion.div} className="dashboard-section-card admin-panel-card" {...cardTransition}>
            <Typography className="section-title">Ejecuciones recientes</Typography>
            <Box className="activity-table-wrap">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Proceso</TableCell>
                    <TableCell>Tipo</TableCell>
                    <TableCell>Inicio</TableCell>
                    <TableCell>Estado</TableCell>
                    <TableCell align="right">Registros</TableCell>
                    <TableCell>Duración</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleExecutions.map((execution, index) => (
                    <TableRow component={motion.tr} key={execution.id} hover {...listItemTransition(index)}>
                      <TableCell>{execution.process}</TableCell>
                      <TableCell className="admin-execution-type">{execution.type}</TableCell>
                      <TableCell>{execution.startLabel}</TableCell>
                      <TableCell>
                        <Chip size="small" className={`admin-status-chip admin-status-${execution.tone}`} label={execution.status} />
                      </TableCell>
                      <TableCell align="right">{execution.records}</TableCell>
                      <TableCell>{execution.duration}</TableCell>
                    </TableRow>
                  ))}
                  {!loading && visibleExecutions.length === 0 && (
                    <TableRow><TableCell colSpan={6}><Typography className="admin-user-empty">No hay ejecuciones registradas hoy.</Typography></TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
            <AdminFooterLink path="/admin/procesos-hoy">Ver todas las ejecuciones</AdminFooterLink>
          </Box>
        </Grid>

        <Grid size={{ xs: 12, xl: 7 }}>
          <Box component={motion.div} className="dashboard-section-card admin-panel-card" {...cardTransition}>
            <Typography className="section-title">Acceso rápido a aplicaciones</Typography>
            <Box className="admin-quick-apps">
              {quickApps.map((app, index) => (
                <Box
                  component={motion.div}
                  key={app.id || app.code}
                  {...listItemTransition(index)}
                >
                  <Box
                    component={app.external ? 'a' : NavLink}
                    {...(app.external ? { href: app.path, target: '_blank', rel: 'noopener noreferrer' } : { to: app.path })}
                    className="admin-quick-app"
                  >
                    <Box className="admin-quick-app-icon">{app.icon}</Box>
                    <Typography>{app.title}</Typography>
                  </Box>
                </Box>
              ))}
              {quickApps.length === 0 && <Typography className="admin-user-empty">No tienes aplicaciones asignadas.</Typography>}
            </Box>
            <AdminFooterLink path="/admin/aplicaciones">Ver todas las aplicaciones</AdminFooterLink>
          </Box>
        </Grid>

        <Grid size={{ xs: 12, md: 6, xl: 2.5 }}>
          <Box component={motion.div} className="dashboard-section-card admin-panel-card" {...cardTransition}>
            <Box className="admin-section-title-row">
              <Typography className="section-title">Alertas activas</Typography>
              <Chip size="small" className="admin-alert-count" label={activeAlerts.length} />
            </Box>
            <Box className="admin-alert-list">
              {activeAlerts.map((alert, index) => (
                <Box component={motion.div} key={alert.id} className="admin-alert-item" {...listItemTransition(index)}>
                  <Box className={`admin-alert-icon admin-alert-${alert.tone}`}>{alert.icon}</Box>
                  <Box>
                    <Typography>{alert.process}</Typography>
                    {alert.message && <Typography>{alert.message}</Typography>}
                  </Box>
                  <Typography>{alert.time}</Typography>
                </Box>
              ))}
              {!loading && activeAlerts.length === 0 && <Typography className="admin-user-empty">Sin alertas activas.</Typography>}
            </Box>
            <AdminFooterLink path="/admin/procesos-error">Ver todas las alertas</AdminFooterLink>
          </Box>
        </Grid>

        <Grid size={{ xs: 12, md: 6, xl: 2.5 }}>
          <Box component={motion.div} className="dashboard-section-card admin-panel-card" {...cardTransition}>
            <Typography className="section-title">Calendario de próximas ejecuciones</Typography>
            <Box className="admin-schedule-list">
              {schedule.map((item, index) => (
                <Box component={motion.div} key={item.id} className="admin-schedule-item" {...listItemTransition(index)}>
                  <Typography>{item.time}</Typography>
                  <Typography>{item.process}</Typography>
                </Box>
              ))}
              {!loading && schedule.length === 0 && <Typography className="admin-user-empty">Sin próximas ejecuciones programadas.</Typography>}
            </Box>
            <AdminFooterLink path="/admin/proximas-ejecuciones">Ver calendario completo</AdminFooterLink>
          </Box>
        </Grid>
      </Grid>
    </Box>
  )
}
